import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { BillingFeature, PopupEvent } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIcon from 'webapp/components/common/ui-icon';
import CustomMetricsEditActionsHorizonComponent from 'webapp/components/custom-metrics/edit/actions/horizon';
import CustomMetricsEditLandingCustomQueryComponent from 'webapp/components/custom-metrics/edit/landing-custom-query';
import CustomMetricsEditLandingScriptComponent from 'webapp/components/custom-metrics/edit/landing-script';
import CustomQuerySetupCommonFormComponent from 'webapp/components/custom-query/setup/common/form';
import CustomQuerySetupCommonSourceSelectComponent from 'webapp/components/custom-query/setup/common/source-select';
import CustomQuerySetupHorizonComponent from 'webapp/components/custom-query/setup/horizon/horizon';
import CustomQuerySetupPossibleWizardComponent from 'webapp/components/custom-query/setup/possible-wizard';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import ScriptsEditComponent from 'webapp/components/scripts/edit';

import type { CustomMetric } from 'webapp/models-custom';
import CustomQuery from 'webapp/models-custom/custom-query';
import HorizonMetric from 'webapp/models-custom/horizon-metric';
import Script from 'webapp/models-custom/script';

export default class CustomMetricsEditPopupComponent extends PopupInnerModalComponent<PopupData.CustomMetric.Edit> {
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/horizon-metrics')
	declare mmHorizonMetricsService: Service.ModelManager.HorizonMetrics;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;
	@service('popup') declare popupService: Service.Popup;

	@tracked modelForFullForm: CustomMetric | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const presetModel = this.popupData.model;
		if (presetModel) {
			this.modelForFullForm = presetModel;
			if (presetModel.isNew) {
				// track creation events now if we are skipping "landing"
				if (this.showCustomQueryForm) {
					this.trackNewQueryEvent();
				} else if (this.showScriptForm) {
					this.trackNewScriptEvent();
				}
			}
		} else {
			this.analyticsService.event(TrackingEvent.NEW_CUSTOM_METRIC, {
				origin: this.popupData.origin,
			});
		}

		this.args.model.on(PopupEvent.CLOSED, this, () => {
			// since we can edit scripts in edit scripts (two popups), cleanup only on the last one
			if (!this.popupService.isOpen(CustomMetricsEditPopupComponent)) {
				this.mmScriptsService.cleanupTemporaryFakeDataselects();
			}
		});
	}

	get isEditing(): boolean {
		return !!this.modelForFullForm && !this.modelForFullForm.isNew;
	}

	get popupTitle(): string {
		return this.isEditing ? 'Edit custom metric' : 'Create a custom metric';
	}

	get contentClass(): string {
		if (this.showLanding) {
			return 'content--landing';
		}
		if (this.showCustomQueryForm) {
			return 'content--custom-query';
		}
		if (this.showScriptForm) {
			return 'content--script';
		}
		if (this.showHorizonForm) {
			return 'content--custom-query';
		}
		return '';
	}

	get showLanding() {
		return !this.modelForFullForm;
	}

	get showCustomQueryForm() {
		return this.isModelCustomQuery(this.modelForFullForm);
	}

	get showScriptForm() {
		return this.isModelScript(this.modelForFullForm);
	}

	get modelAsCustomQuery() {
		return this.modelForFullForm as CustomQuery;
	}

	get modelAsScript() {
		return this.modelForFullForm as Script;
	}

	get viewOnly() {
		// after duplication in view only mode, the model is replaced with a new unsaved clone, so the form should be editable
		if (this.modelForFullForm?.isNew) {
			return false;
		}
		return !!this.popupData.viewOnly;
	}

	private isModelCustomQuery(model: CustomMetric | null): model is CustomQuery {
		return model instanceof CustomQuery;
	}

	get showHorizonForm() {
		return this.isModelHorizonMetric(this.modelForFullForm);
	}

	get isHorizonBeta(): boolean {
		// Edit mode: the model is a HorizonMetric directly
		if (this.showHorizonForm) {
			const source = this.mmSourcesService.sourceModels.find(
				(s) => s.id === String(this.modelAsHorizonMetric.spaceAccessId),
			);
			return !!source?.sourceType?.isHorizonBeta && !source?.isDataset;
		}
		// Create mode: the model is a CustomQuery, and the horizon builder
		// renders inside CustomQuerySetupCommonFormComponent when the source
		// supports horizon. showHorizonForm is false because the model is
		// not a HorizonMetric yet — it becomes one only after the first save.
		if (this.showCustomQueryForm && this.modelAsCustomQuery.isNew) {
			const source = this.mmSourcesService.sourceModels.find(
				(s) => s.id === String(this.modelAsCustomQuery.sourceId),
			);
			return (
				!!source?.sourceType?.isHorizonSupported &&
				!!source?.sourceType?.isHorizonBeta &&
				!source?.isDataset
			);
		}
		return false;
	}

	get modelAsHorizonMetric() {
		return this.modelForFullForm as HorizonMetric;
	}

	get isClone(): boolean {
		return !!this.popupData.isClone;
	}

	get horizonEditMetricId(): number | undefined {
		return this.modelAsHorizonMetric.numericId ?? undefined;
	}

	get horizonEditMetricName(): string {
		const name = this.modelAsHorizonMetric.metricName;
		return this.isClone ? `Copy of ${name}` : name;
	}

	private isModelHorizonMetric(model: CustomMetric | null): model is HorizonMetric {
		return model instanceof HorizonMetric;
	}

	private isModelScript(model: CustomMetric | null): model is Script {
		return model instanceof Script;
	}

	@action
	continueAsCustomQuery(model: CustomQuery) {
		this.modelForFullForm = model;
		this.trackNewQueryEvent();
	}

	@action
	continueAsScript(model: Script) {
		this.modelForFullForm = model;
		this.trackNewScriptEvent();
	}

	@action
	onClosingQuery(query?: CustomQuery, isNew?: boolean, isDeleted?: boolean) {
		if (isDeleted && typeof this.popupData.onDelete === 'function') {
			this.popupData.onDelete();
		} else if (query) {
			if (typeof this.popupData.afterSave === 'function') {
				this.popupData.afterSave(query);
			} else if (isNew && this.popupData.origin === 'listing') {
				this.mmCustomQueriesService.createBlockFromQuery(query);
			}
		} else if (!isDeleted && typeof this.popupData.afterSave === 'function') {
			this.popupData.afterSave(this.modelForFullForm!);
		}

		this.close();
	}

	@action
	onSaveHorizonMetric(): void {
		const isNew = !this.horizonEditMetricId;
		if (typeof this.popupData.afterSave === 'function') {
			this.popupData.afterSave(this.modelAsHorizonMetric);
		} else if (isNew && this.popupData.origin === 'listing') {
			this.mmHorizonMetricsService.createBlockFromHorizonMetric(this.modelAsHorizonMetric);
		}
		this.close();
	}

	@action
	async cloneQuery() {
		if (this.billingService.checkIfCan(BillingFeature.CUSTOM_METRICS, true)) {
			await this.prepareForClonedPopup();

			this.mmCustomQueriesService.cloneQuery(
				this.modelAsCustomQuery,
				'',
				this.popupData.afterSave,
			);

			this.scheduleClose();
		}
	}

	@action
	async cloneScript() {
		await this.prepareForClonedPopup();

		this.mmScriptsService.cloneScript(this.modelAsScript, '', this.popupData.afterSave);

		this.scheduleClose();
	}

	@action
	changeSource(): void {
		// Source is immutable for Horizon metrics — select is always disabled in edit mode
	}

	@action
	cloneHorizonMetric(): void {
		this.mmHorizonMetricsService.cloneMetric(this.modelAsHorizonMetric);
		this.scheduleClose();
	}

	@action
	deleteHorizonMetric(): void {
		this.mmHorizonMetricsService.deleteMetric(this.modelAsHorizonMetric, () => {
			if (typeof this.popupData.onDelete === 'function') {
				this.popupData.onDelete();
			}
			this.close();
		});
	}

	private async prepareForClonedPopup() {
		if (this.navigationService.activatedRoute.startsWith('metrics.edit')) {
			await this.navigationService.transitionToRoute('metrics');
		}
	}

	private scheduleClose() {
		later(() => {
			this.close();
		}, 2000);
	}

	private trackNewQueryEvent() {
		const source = this.modelAsCustomQuery.sourceObj;
		const type = source?.isTypeOfDataset ? source.selectedSourceOption?.slice(0, -1) : null;
		this.analyticsService.event(TrackingEvent.NEW_QUERY, {
			origin: this.popupData.origin,
			type,
		});
	}

	private trackNewScriptEvent() {
		this.analyticsService.event(TrackingEvent.NEW_CALCULATION, {
			origin: this.popupData.origin,
		});
	}

	<template>
		<div class="title">
			<CommonUiIcon @name="MetricSettings" @size="lg" />
			{{this.popupTitle}}
			{{#if this.isHorizonBeta}}
				<div class="beta-tag">Beta</div>
			{{/if}}
		</div>

		<div class="content {{this.contentClass}}">

			{{#if this.showLanding}}

				<div class="form theme-dark tw:dark">

					<CustomMetricsEditLandingCustomQueryComponent
						@onSourceSelect={{this.continueAsCustomQuery}}
					/>

					<CustomMetricsEditLandingScriptComponent
						@onItemSelect={{this.continueAsScript}}
					/>

				</div>

			{{else if this.showCustomQueryForm}}

				{{#if this.modelAsCustomQuery.isWizardPossible}}
					<CustomQuerySetupPossibleWizardComponent
						@model={{this.modelAsCustomQuery}}
						@origin={{this.popupData.origin}}
						@inPopup={{true}}
						@onFormClose={{this.onClosingQuery}}
						@cloneQuery={{this.cloneQuery}}
						@openMetricDefinitionPopupOnInit={{this.popupData.openMetricDefinitionPopupOnInit}}
						@viewOnly={{this.viewOnly}}
					/>
				{{else}}
					<CustomQuerySetupCommonFormComponent
						@model={{this.modelAsCustomQuery}}
						@origin={{this.popupData.origin}}
						@inPopup={{true}}
						@onFormClose={{this.onClosingQuery}}
						@cloneQuery={{this.cloneQuery}}
						@openMetricDefinitionPopupOnInit={{this.popupData.openMetricDefinitionPopupOnInit}}
						@viewOnly={{this.viewOnly}}
					/>
				{{/if}}

			{{else if this.showHorizonForm}}

				<div class="horizon-metric-builder-flow">
					<div
						class="form form--custom-query theme-dark tw:dark form--visible horizon-form-header"
					>
						<div class="form__top-bar">
							<CustomQuerySetupCommonSourceSelectComponent
								@sourceId={{this.modelAsHorizonMetric.spaceAccessId}}
								@isDisabled={{true}}
								@onChange={{this.changeSource}}
							/>
							<CustomMetricsEditActionsHorizonComponent
								@isNew={{false}}
								@sourceTypeKey={{this.modelAsHorizonMetric.sourceObj.sourceType.key}}
								@duplicate={{this.cloneHorizonMetric}}
								@delete={{this.deleteHorizonMetric}}
							/>
						</div>
					</div>
					<CustomQuerySetupHorizonComponent
						@sourceId={{this.modelAsHorizonMetric.spaceAccessId}}
						@onSave={{this.onSaveHorizonMetric}}
						@onCancel={{this.close}}
						@editMetricId={{this.horizonEditMetricId}}
						@editMetricName={{this.horizonEditMetricName}}
						@isClone={{this.isClone}}
						@viewOnly={{this.viewOnly}}
					/>
				</div>

			{{else if this.showScriptForm}}

				<ScriptsEditComponent
					@popup={{@model}}
					@model={{this.modelAsScript}}
					@origin={{this.popupData.origin}}
					@openMetricDefinitionPopupOnInit={{this.popupData.openMetricDefinitionPopupOnInit}}
					@afterSave={{this.popupData.afterSave}}
					@cloneScript={{this.cloneScript}}
					@close={{this.close}}
				/>

			{{/if}}

		</div>
	</template>
}
